import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Heart, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { humanizeError } from '../../utils/errorHandling';
import {
  LOVED_CATEGORIES,
  LOVED_SECTION_SUBTITLE,
  LOVED_SECTION_TITLE,
  blankLovedItem,
  groupLovedItemsByCategory,
  lovedCategorySingular,
  normalizeLovedItemDraft,
} from '../../utils/lovedItems';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LovedItemCard({ item, canEdit, onEdit, onDelete }) {
  const linkLabel = item.link_label || 'Open link';
  return (
    <li
      className="group rounded-xl border border-[hsl(38_14%_86%)]/55 bg-white/50 px-4 py-3.5 shadow-[inset_0_1px_0_0_hsla(0,0%,100%,0.7)]"
      data-testid={`loved-item-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-serif text-[0.98rem] text-foreground/90">{item.title}</p>
          {item.note ? (
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground/85">{item.note}</p>
          ) : null}
          {item.link_url ? (
            <a
              href={item.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--studio-accent,38_36%_42%))] underline-offset-4 hover:underline"
            >
              {linkLabel}
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          ) : null}
        </div>
        {canEdit ? (
          <div className="flex shrink-0 gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(item)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              <span className="sr-only">Edit</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive/80 hover:text-destructive"
              onClick={() => onDelete(item)}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              <span className="sr-only">Remove</span>
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

export function WhatTheyLovedSection({ personId, items = [], canEdit = false, onUpdated }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(() => blankLovedItem());
  const [saving, setSaving] = useState(false);

  const grouped = useMemo(() => groupLovedItemsByCategory(items), [items]);
  const hasContent = items.length > 0;

  const persistItems = async (nextItems) => {
    if (!personId) return;
    setSaving(true);
    try {
      const response = await axios.put(`${API}/individuals/${personId}`, { loved_items: nextItems });
      onUpdated?.(response.data?.loved_items || nextItems);
      toast.success('Saved what they loved.');
    } catch (err) {
      toast.error(humanizeError(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setDraft(blankLovedItem());
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setDraft({
      ...blankLovedItem(item.category),
      ...item,
      note: item.note || '',
      link_url: item.link_url || '',
      link_label: item.link_label || '',
    });
    setDialogOpen(true);
  };

  const handleSaveDraft = async () => {
    const normalized = normalizeLovedItemDraft(draft);
    if (!normalized) {
      toast.error('Please add a title.');
      return;
    }
    const exists = items.some((i) => i.id === normalized.id);
    const next = exists
      ? items.map((i) => (i.id === normalized.id ? normalized : i))
      : [...items, normalized];
    try {
      await persistItems(next);
      setDialogOpen(false);
    } catch {
      /* toast handled */
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.title}" from what they loved?`)) return;
    const next = items.filter((i) => i.id !== item.id);
    try {
      await persistItems(next);
    } catch {
      /* toast handled */
    }
  };

  if (!hasContent && !canEdit) return null;

  return (
    <>
      <Card
        className="border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)]/90"
        data-testid="what-they-loved-section"
      >
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 font-serif text-lg">
                <Heart className="h-4 w-4 text-[hsl(var(--studio-accent,38_36%_42%))]" aria-hidden />
                {LOVED_SECTION_TITLE}
              </CardTitle>
              <CardDescription className="mt-1">{LOVED_SECTION_SUBTITLE}</CardDescription>
            </div>
            {canEdit ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 rounded-full border-[#8B7355]/30 text-[#5c4a32] hover:bg-[#8B7355]/8"
                onClick={openCreate}
                data-testid="add-loved-item-btn"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add a favourite
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {!hasContent ? (
            <p className="font-serif text-sm leading-relaxed text-muted-foreground/85">
              Capture a song that followed them through life, a book they pressed into your hands, or the restaurant
              where they always ordered the same thing.
            </p>
          ) : (
            grouped.map((group) => (
              <div key={group.id}>
                <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {group.label}
                </p>
                <ul className="space-y-2.5">
                  {group.items.map((item) => (
                    <LovedItemCard
                      key={item.id}
                      item={item}
                      canEdit={canEdit}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </ul>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="loved-item-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {draft.id && items.some((i) => i.id === draft.id) ? 'Edit favourite' : 'Add a favourite'}
            </DialogTitle>
            <DialogDescription>
              Optional links open externally — Spotify, IMDb, a map, or anywhere that helps the family remember.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={draft.category} onValueChange={(v) => setDraft((d) => ({ ...d, category: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOVED_CATEGORIES.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.singular}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                placeholder="What they loved"
                className="font-serif"
              />
            </div>
            <div className="space-y-2">
              <Label>Short note (optional)</Label>
              <Textarea
                value={draft.note}
                onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                rows={2}
                placeholder="Why it mattered, when they shared it, a small detail…"
                className="font-serif text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>External link (optional)</Label>
              <Input
                value={draft.link_url}
                onChange={(e) => setDraft((d) => ({ ...d, link_url: e.target.value }))}
                placeholder="https://…"
                type="url"
              />
            </div>
            <div className="space-y-2">
              <Label>Link label (optional)</Label>
              <Input
                value={draft.link_label}
                onChange={(e) => setDraft((d) => ({ ...d, link_label: e.target.value }))}
                placeholder="Spotify, IMDb, Google Maps…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default WhatTheyLovedSection;
